import type { z } from 'zod'
import { runtimeConfig } from '@/shared/config'

/**
 * Ошибка API в разобранном виде. На проводе живут две формы:
 * доменные отказы бэкенда несут машинный `code` (`EXCURSION_FINISHED`),
 * ошибки самого фреймворка (валидация запроса, неизвестный маршрут) — нет.
 * Код нужен, чтобы интерфейс различал случаи, не разбирая человеческий текст.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Человеческий текст с сервера — он уже на русском и годится как фолбэк. */
  readonly detail: string

  constructor(status: number, code: string, detail: string) {
    super(detail || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

type QueryValue = string | number | boolean | string[] | undefined | null

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  /**
   * Публичный контур записи. Отдельный флаг оставлен намеренно: контур
   * анонимный по смыслу, и это должно быть видно в месте вызова, даже когда
   * фронт и API живут на одном origin.
   */
  anonymous?: boolean
  signal?: AbortSignal
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = runtimeConfig.API_BASE_URL.replace(/\/$/, '')
  const url = new URL(`${base}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      // Повторяющийся ключ, а не список через запятую: huma разбирает `?k=a&k=b`.
      for (const item of value) url.searchParams.append(key, item)
    } else {
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1]
}

interface WireError {
  code?: string
  detail?: string
  title?: string
  errors?: { message?: string }[]
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = ''
  let detail = ''
  try {
    const payload = (await response.json()) as WireError
    code = payload.code ?? ''
    // Ошибки валидации фреймворка кода не несут — собираем текст из errors[].
    detail =
      payload.detail ??
      payload.errors
        ?.map((e) => e.message)
        .filter(Boolean)
        .join('; ') ??
      payload.title ??
      ''
  } catch {
    // тело не JSON (502 от прокси, обрыв) — остаёмся на статусе
  }
  return new ApiError(response.status, code, detail)
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers = new Headers({ Accept: 'application/json' })
  if (options.body !== undefined) headers.set('Content-Type', 'application/json')

  if (!options.anonymous && method !== 'GET') {
    const csrf = readCookie('csrf_token')
    if (csrf) headers.set('X-CSRF-Token', csrf)
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    // Фронт и API на одном origin (Traefik разводит по пути), поэтому запрос не
    // межсайтовый. Флаг оставлен как явное «сессия участвует».
    credentials: options.anonymous ? 'omit' : 'include',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (!response.ok) throw await toApiError(response)
  return response
}

/**
 * Запрос с разбором ответа схемой. Успешные ответы завёрнуты в `{"data": …}` —
 * конверт снимается здесь, чтобы схемы описывали полезную нагрузку, а каждый
 * хук не помнил про обёртку.
 */
export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await send(path, options)
  const payload = (await response.json()) as { data?: unknown }
  return schema.parse(payload.data)
}

/** Запрос без тела ответа (204). */
export async function requestVoid(path: string, options: RequestOptions = {}): Promise<void> {
  await send(path, options)
}
